import * as React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { APIs } from "../../apis";
import {
  type AdditionalChargeRequest,
  type OrderDetail,
  type OrderDetailItem,
  TpaAdditionalServiceApprovalStatus,
  type TpaAdditionalServicesActionPayload,
} from "../../apis/order";
import { useApi, useNavigation } from "../../hooks";
import { SVGs } from "../../assets";
import {
  type CardAdvanceModel,
  type CardAdvanceRowProps,
} from "../../components";

export type BookingDetailCharges = {
  additionalChargeRequests: AdditionalChargeRequest[];
};

function getBookingDetailCharges(
  order?: OrderDetail,
): BookingDetailCharges | undefined {
  const additionalChargeRequests = order?.additionalChargeRequests ?? [];
  if (!additionalChargeRequests.length || !order) return undefined;

  return { additionalChargeRequests };
}

export function useViewModel() {
  const { t } = useTranslation();
  const { setOptions, goBack } = useNavigation();
  const { orderId = "" } = useParams<{ orderId: string }>();
  const { fetch, data, loading } =
    useApi<Awaited<ReturnType<typeof APIs.order.getOrder>>>();
  const actionApi = useApi();

  const order = data?.order;

  const loadOrder = React.useCallback(async () => {
    if (!orderId) return;
    try {
      const result = await fetch(() => APIs.order.getOrder(orderId), {
        resetDataOnError: true,
      });
      setOptions({ title: result?.order?.code });
    } catch {
      goBack();
    }
  }, [fetch, orderId, setOptions, goBack]);

  React.useEffect(() => {
    void loadOrder();
  }, [loadOrder]);

  const memberCards: CardAdvanceModel[] = React.useMemo(() => {
    return (order?.order_items ?? []).map(
      (item: OrderDetailItem, index: number) => ({
        title:
          (order?.order_items?.length ?? 0) > 1
            ? t("general.memberInformationIndexed", { index: index + 1 })
            : t("general.memberInformation"),
        items: [
          {
            icon: SVGs.avatarRound,
            value:
              `${item.visitorFirstName ?? ""} ${item.visitorLastName ?? ""}`.trim() ||
              "-",
          },
          { icon: SVGs.envelope, value: item.visitorEmailAddress ?? "-" },
          { icon: SVGs.telephone, value: item.visitorPhoneNumber ?? "-" },
          (item.insuranceProviderName || item.insurancePolicyNumber) && {
            icon: SVGs.barcode,
            value: `${item.insuranceProviderName || "-"} - ${item.insurancePolicyNumber || "-"}`,
          },
        ].filter(Boolean) as CardAdvanceRowProps[],
      }),
    );
  }, [order?.order_items, t]);

  const billingCard: CardAdvanceModel | undefined = React.useMemo(() => {
    if (!order) return undefined;
    return {
      title: t("general.billingDetails"),
      items: [
        {
          icon: SVGs.avatarRound,
          value:
            `${order.billingPersonFirstName ?? ""} ${order.billingPersonLastName ?? ""}`.trim() ||
            "-",
        },
        { icon: SVGs.envelope, value: order.billingPersonEmail || "-" },
        {
          icon: SVGs.telephone,
          value: order.billingPersonContactNumber || "-",
        },
      ],
    };
  }, [order, t]);

  const bookerCard: CardAdvanceModel | undefined = React.useMemo(() => {
    if (!order?.bookerFirstName && !order?.bookerLastName) return undefined;
    return {
      title: t("general.bookerInformation"),
      items: [
        {
          icon: SVGs.avatarRound,
          value:
            `${order.bookerFirstName ?? ""} ${order.bookerLastName ?? ""}`.trim() ||
            "-",
        },
      ],
    };
  }, [order, t]);

  const infoCards = React.useMemo(
    () =>
      [...memberCards, bookerCard, billingCard].filter(
        Boolean,
      ) as CardAdvanceModel[],
    [billingCard, bookerCard, memberCards],
  );

  const charges = React.useMemo(() => getBookingDetailCharges(order), [order]);

  const submitApproval = React.useCallback(
    async (
      status: TpaAdditionalServiceApprovalStatus,
      payload?: TpaAdditionalServicesActionPayload,
    ) => {
      if (!orderId) return;
      try {
        await actionApi.fetch(() =>
          APIs.order.updateAdditionalServiceApproval(orderId, status, payload),
        );
        await loadOrder();
      } catch {}
    },
    [actionApi, loadOrder, orderId],
  );

  return {
    t,
    order,
    loading: loading || actionApi.loading,
    charges,
    infoCards,
    submitApproval,
  };
}

export type BookingDetailViewModel = ReturnType<typeof useViewModel>;
